import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listEpicorInvoices } from '../../services/epicorApi';
import { Button } from '../ui';
import { fmt, fmtAmt, fmtDate } from '../orders/orderFormat';

const PAGE_SIZE = 50;

const Corners = () => (
  <>
    <i className="oc-corner tl" aria-hidden="true" />
    <i className="oc-corner tr" aria-hidden="true" />
    <i className="oc-corner bl" aria-hidden="true" />
    <i className="oc-corner br" aria-hidden="true" />
  </>
);

const statusLabel = (i) => {
  if (!i.posted) return 'Unposted';
  if (i.open_invoice) return 'Open';
  return 'Settled';
};

const InvoicesPage = () => {
  const navigate = useNavigate();

  const [companyFilter, setCompanyFilter] = useState('');
  const [docType, setDocType] = useState('all');
  const [openOnly, setOpenOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [rowsLoading, setRowsLoading] = useState(true);

  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    setLoadError(null);
    try {
      const data = await listEpicorInvoices({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        company: companyFilter || undefined,
        doc_type: docType,
        open_only: openOnly || undefined,
        search: search || undefined,
      });
      setRows(data.invoices || []);
      setTotal(data.total || 0);
    } catch (e) {
      setLoadError(e.response?.data?.detail || e.message);
    } finally {
      setRowsLoading(false);
    }
  }, [page, companyFilter, docType, openOnly, search]);

  useEffect(() => { loadRows(); }, [loadRows]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchDraft.trim().length >= 2 || searchDraft.trim().length === 0
        ? searchDraft.trim()
        : search;
      if (next !== search) {
        setSearch(next);
        setPage(0);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [searchDraft, search]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="customers-page oc-scope">
      <header className="oc-header">
        <div>
          <h1 className="oc-title">Invoices &amp; credit notes</h1>
          <p className="oc-blurb">
            Archival Epicor invoices. About half of all credit notes have no order link,
            so this list — not the order pages — is the way to find them.
          </p>
        </div>
      </header>

      <div className="oc-frame oc-blueprint" style={{ marginTop: 24 }}>
        <Corners />
        <div className="oc-toolbar" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <select
            value={companyFilter}
            onChange={(e) => { setCompanyFilter(e.target.value); setPage(0); }}
          >
            <option value="">All companies</option>
            <option value="AU">AU</option>
            <option value="NZ">NZ</option>
          </select>
          <select
            value={docType}
            onChange={(e) => { setDocType(e.target.value); setPage(0); }}
          >
            <option value="all">All documents</option>
            <option value="invoice">Invoices</option>
            <option value="credit_memo">Credit notes</option>
          </select>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={openOnly}
              onChange={(e) => { setOpenOnly(e.target.checked); setPage(0); }}
            />
            Open only
          </label>
          <input
            type="search"
            placeholder="Invoice #, PO, or customer…"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            style={{ minWidth: 220 }}
          />
          <span className="oc-hint">{fmt(total)} documents</span>
        </div>

        {loadError && <p className="oc-error">{loadError}</p>}
        <table className="oc-table">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Date</th>
              <th>Due</th>
              <th>Customer</th>
              <th>Status</th>
              <th>Total</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {rowsLoading && <tr><td colSpan={7}>Loading…</td></tr>}
            {!rowsLoading && rows.length === 0 && (
              <tr><td colSpan={7}>No invoices found.</td></tr>
            )}
            {rows.map((i) => (
              <tr
                key={`${i.company}-${i.invoice_num}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/invoices/${i.company}/${i.invoice_num}`)}
              >
                <td>
                  <strong>{i.company} {i.invoice_num}</strong>
                  {i.credit_memo ? <div className="oc-hint">Credit note</div> : null}
                  {i.po_num ? <div className="oc-hint">PO {i.po_num}</div> : null}
                </td>
                <td>{fmtDate(i.invoice_date)}</td>
                <td>{fmtDate(i.due_date)}</td>
                <td>
                  {i.cust_id || i.cust_num || '—'}
                  {i.customer_name ? <div className="oc-hint">{i.customer_name}</div> : null}
                </td>
                <td>{statusLabel(i)}</td>
                {/* Epicor already stores credit notes negative — do not negate here. */}
                <td>{fmtAmt(i.doc_invoice_amt)}</td>
                <td>{fmtAmt(i.doc_invoice_bal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="oc-pager">
          <Button disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span>Page {page + 1} / {pages}</span>
          <Button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
};

export default InvoicesPage;
